import React, { Fragment } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Dialog, Transition } from '@headlessui/react'

import { HomeIcon, ChartBarIcon, UserCircleIcon, PlusCircleIcon, XMarkIcon, ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import piggybankLogo from '../assets/blue_pig.png';


interface SidebarProps {
    isOpen: boolean;
    setOpen: (isOpen: boolean) => void;
    isCollapsed: boolean;
    setCollapsed: (isCollapsed: boolean) => void;
}


export default function Sidebar({ isOpen, setOpen, isCollapsed, setCollapsed }: SidebarProps) {
  const location = useLocation();
  const { user } = useAuth();
  
  const navigation = [
    { name: 'home', href: '/', icon: HomeIcon, current: location.pathname === '/' },
    { name: 'market', href: '/market', icon: ChartBarIcon, current: location.pathname === '/market' },
    { name: 'create coin', href: '/create-token', icon: PlusCircleIcon, current: location.pathname === '/create-token' },
  ]

  const userNavigation = [
    { name: 'profile', href: '/profile', icon: UserCircleIcon, current: location.pathname === '/profile' },
    // { name: 'deposit', href: '/deposit', icon: BanknotesIcon, current: location.pathname === '/deposit' },
  ]

  const sidebarContent = (isMobile: boolean) => (
    <div className={`flex grow flex-col gap-y-2 overflow-y-auto bg-slate-900 px-2 pb-4 ${!isMobile ? 'border-r border-accent-blue' : ''}`}>
        <div className={`flex h-16 shrink-0 items-center ${isCollapsed && !isMobile ? 'justify-center' : 'px-4'}`}>
            <img
                className="h-8 w-auto"
                src={piggybankLogo}
                alt="Piggybank"
            />
             {(!isCollapsed || isMobile) && <span className="text-2xl font-extrabold text-accent-blue tracking-tight ml-2">Piggybank</span>}
        </div>
        
        {!isMobile && (
            <div className="w-full flex justify-center">
                 <button
                    onClick={() => setCollapsed(!isCollapsed)}
                    className="p-1 text-slate-400 hover:text-white focus:outline-none"
                    aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
                >
                    {isCollapsed ? <ChevronRightIcon className="h-5 w-5" /> : <ChevronLeftIcon className="h-5 w-5" />}
                </button>
            </div>
        )}

        <nav className="flex flex-1 flex-col justify-between">
            <div>
                <ul className="space-y-1">
                    {navigation.map((item) => (
                        <li key={item.name}>
                            <Link
                                to={item.href}
                                className={`group flex gap-x-3 rounded-md p-2 text-sm leading-6 font-semibold items-center ${isCollapsed && !isMobile ? 'justify-center' : ''} ${item.current ? 'bg-slate-800 text-white' : 'text-slate-400 hover:text-white hover:bg-slate-800'}`}
                            >
                                <item.icon className={`h-6 w-6 shrink-0 ${item.current ? 'text-white' : 'text-slate-500 group-hover:text-white'}`} aria-hidden="true" />
                                {(!isCollapsed || isMobile) && <span className="truncate">{item.name}</span>}
                            </Link>
                        </li>
                    ))}
                </ul>
            </div>
            {user && (
                <div>
                    {(!isCollapsed || isMobile) && <div className="text-xs font-semibold leading-6 text-slate-500 px-2">Your Account</div>}
                    <ul className={`space-y-1 mt-2`}>
                        {userNavigation.map((item) => (
                            <li key={item.name}>
                                <Link
                                    to={item.href}
                                    className={`group flex gap-x-3 rounded-md p-2 text-sm leading-6 font-semibold items-center ${isCollapsed && !isMobile ? 'justify-center' : ''} ${item.current ? 'bg-slate-800 text-white' : 'text-slate-400 hover:text-white hover:bg-slate-800'}`}
                                >
                                    <item.icon className={`h-6 w-6 shrink-0 ${item.current ? 'text-white' : 'text-slate-500 group-hover:text-white'}`} aria-hidden="true" />
                                    {(!isCollapsed || isMobile) && <span className="truncate">{item.name}</span>}
                                </Link>
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </nav>
    </div>
  )


  return (
    <>
        {/* Mobile sidebar */}
        <Transition.Root show={isOpen} as={Fragment}>
            <Dialog as="div" className="relative z-50 lg:hidden" onClose={setOpen}>
                <Transition.Child
                    as={Fragment}
                    enter="transition-opacity ease-linear duration-300"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="transition-opacity ease-linear duration-300"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <div className="fixed inset-0 bg-gray-900/80" />
                </Transition.Child>

                <div className="fixed inset-0 flex">
                    <Transition.Child
                        as={Fragment}
                        enter="transition ease-in-out duration-300 transform"
                        enterFrom="-translate-x-full"
                        enterTo="translate-x-0"
                        leave="transition ease-in-out duration-300 transform"
                        leaveFrom="translate-x-0"
                        leaveTo="-translate-x-full"
                    >
                        <Dialog.Panel className="relative mr-16 flex w-full max-w-xs flex-1">
                            <Transition.Child
                                as={Fragment}
                                enter="ease-in-out duration-300"
                                enterFrom="opacity-0"
                                enterTo="opacity-100"
                                leave="ease-in-out duration-300"
                                leaveFrom="opacity-100"
                                leaveTo="opacity-0"
                            >
                                <div className="absolute left-full top-0 flex w-16 justify-center pt-5">
                                    <button type="button" className="-m-2.5 p-2.5" onClick={() => setOpen(false)}>
                                        <span className="sr-only">Close sidebar</span>
                                        <XMarkIcon className="h-6 w-6 text-white" aria-hidden="true" />
                                    </button>
                                </div>
                            </Transition.Child>
                            {sidebarContent(true)}
                        </Dialog.Panel>
                    </Transition.Child>
                </div>
            </Dialog>
        </Transition.Root>

        {/* Static sidebar for desktop */}
        <div className={`hidden lg:fixed lg:inset-y-0 lg:z-50 lg:flex lg:flex-col transition-all duration-300 ${isCollapsed ? 'lg:w-20' : 'lg:w-48'}`}>
           {sidebarContent(false)}
        </div>
    </>
  );
} 